import React, { useState } from 'react';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LANGUAGE_NAMES = {
  it: 'Italiano',
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français',
};

/**
 * Reusable language selector badge — positioned top-right in any page.
 * Renders a globe pill button that opens a dropdown with all 6
 * supported languages. Picking a language marks the choice as MANUAL
 * (so geo-detection won't override it on next app start) via the
 * global setLanguage() from LanguageContext.
 */
export default function LanguageSelectorBadge({ className = '' }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);

  const onPick = (code) => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} data-testid="language-selector-badge">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/20 active:scale-[0.96] transition-all text-white text-sm font-semibold backdrop-blur-sm"
        data-testid="language-selector-toggle"
      >
        <Globe className="w-4 h-4" />
        <span>{(language || 'it').toUpperCase()}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 bg-black/40 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl overflow-hidden z-[70] min-w-[170px]">
            {Object.entries(LANGUAGE_NAMES).map(([code, name]) => (
              <button
                key={code}
                type="button"
                onClick={() => onPick(code)}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition-colors flex items-center justify-between text-white ${
                  language === code ? 'bg-white/5 text-[#E8334A]' : ''
                }`}
                data-testid={`language-option-${code}`}
              >
                {name}
                {language === code && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
